import type {
  FormSubmissionContact,
  FormSubmissionCoordinates,
  FormSubmissionPerson,
  FormSubmissionSelect,
} from "../formSubmission";

export namespace DrainageInjectionWellGeneral {
  //These are meant to map to the tags that we get back from the form submission.
  //The names of the tags are important and it's how we locate the data that we want.
  //If the tag is updated in nForm, it needs to be updated here as well.
  export const TAGS = {
    //Facility section
    facilitySection_Tag: "facilitySection",
    facility_facilityName_Tag: "facilityName",
    facility_permitNumber_Tag: "permitNumber",
    facility_permitExpiration_Tag: "permitExpirationDate",
    facility_uicPermitApplicability_Tag: "uicPermitApplicability",
    facility_facilityInjectionWellSystemStatus_Tag: "facilityStatusDescription",
    facility_epaIdentifier_Tag: "EPANo",

    //Injection for the Entire Drainage System Section
    injectionSection_Tag: "InjectionQuantity",
    injection_wellNumber_Tag: "WellID",
    injection_wellRate_Tag: "injectionRate",
    injection_injectionPressure_Tag: "injectionPressure",
    injection_maxInjectionQuantity_Tag: "MaxInjectionRateGPDDay",
    injection_maximumInjectionRate_Tag: "MaxGPM24Hours",
    injection_injectionManner_Tag: "injectionManner",
    injection_waterTreatmentDescription_Tag: "waterTreatmentDescription",
    //Well Dimensions Section
    wellSection_Tag: "wellDimensionsSection",
    well_wellNumber_Tag: "injectionWellNumber",
    well_groundSurfaceElevation_Tag: "groundSurfaceElevation",
    well_wellDepth_Tag: "totalDepthBelowGroundSurface",
    well_wellCoordinates_Tag: "wellCoordinates",
    well_groundwaterDepth_Tag: "groundwaterDepth",
    well_perforatedCasingDepthStart_Tag: "perforatedCasingDepthStart",
    well_perforatedCasingTotalLength_Tag: "perforatedCasingTotalLength",
    well_openHoleStartDepth_Tag: "openHoleStartDepth",
    well_openHoleTotalLength_Tag: "openHoleTotalLength",
    //LocationSection
    locationSection_Tag: "LocationSection",
    location_facilityCoordinates_Tag: "facilityCoordinates",
    location_quarterMileDrinkingWaterWell_Tag: "quarterMileDrinkingWaterWell",
    location_aboveBelowUicLine_Tag: "aboveBelowUicLine",
    location_taxMapKeyNumber_Tag: "taxMapKeyNumber",
    location_tidallyInfluenced_Tag: "TidalInfluence",
    location_closestSurfaceWaterName_Tag: "SurfaceWaterName",
    location_closestSurfaceWaterDistance_Tag: "SurfaceWaterDistance",
    location_locatedSmaArea_Tag: "SMA_area",
    //SpecialCommentsOrConsiderationsSection
    specialCommentsOrConsiderationsSection_Tag: "specialCommentsConsiderationsSection",
    specialCommentsOrConsiderations_affectPublicTrustNativeHawaiianResource_Tag:
      "affectPublicTrustNativeHawaiianResource",
    specialCommentsOrConsiderations_protectionFeasibleActions_Tag: "protectionFeasibleActions",
    specialCommentsOrConsiderations_additionalInformation_Tag: "additionalInformation",
    //OwnerSection
    ownerSection_Tag: "ownerSection",
    owner_owner_Tag: "Owner",

    //OperatorSection
    operatorSection_Tag: "operatorSection",
    oeprator_operator_Tag: "FacOper",

    //ConsultantSection
    consultantSection_Tag: "consultantSection",
    consultant_consultant_Tag: "Consultant",

    //InjectionSystemSection
    injectionSystemSectionSection_Tag: "injectionSystemSection",
    injectionSystemSection_injectionWellCount_Tag: "injectionWellCount",
    injectionSystemSection_injectedFluidSource_Tag: "injectedFluidSource",
    injectionSystemSection_surfaceAreas_Tag: "surfaceAreas",
    injectionSystemSection_receivingWaterType_Tag: "SalinityType",
  };

  //DrainageInjectionWellPermitRenewalApplication definition
  export interface Application {
    [sectionNames.facilityDetails]?: FacilityDetails;
    [sectionNames.injectionDetails]?: InjectionDetails[];
    [sectionNames.wellDetails]?: WellDetails[];
    [sectionNames.locationDetails]?: LocationDetails;
    [sectionNames.specialCommentsOrConsiderationsDetails]?: SpecialCommentsOrConsiderationsDetails;
    [sectionNames.ownerDetails]?: OwnerDetails;
    [sectionNames.operatorDetails]?: OperatorDetails;
    [sectionNames.consultantDetails]?: ConsultantDetails;
    [sectionNames.injectionSystemDetails]?: InjectionSystemDetails;
  }

  export interface FacilityDetails {
    [facilityPropertyNames.facilityName]?: string;
    [facilityPropertyNames.permitNumber]?: string;
    [facilityPropertyNames.permitExpirationDate]?: string;
    [facilityPropertyNames.uicPermitApplicability]?: FormSubmissionSelect;
    [facilityPropertyNames.facilityInjectionWellSystemStatus]?: any;
    [facilityPropertyNames.epaIdentifier]?: string;
  }

  export interface InjectionDetails {
    [injectionPropertyNames.wellNumber]?: string;
    [injectionPropertyNames.wellRate]?: FormSubmissionSelect;
    [injectionPropertyNames.injectionPressure]?: FormSubmissionSelect;
    [injectionPropertyNames.maxInjectionQuantity]?: string;
    [injectionPropertyNames.maximumInjectionRate]?: string;
    [injectionPropertyNames.injectionManner]?: FormSubmissionSelect;
    [injectionPropertyNames.waterTreatmentDescription]?: string;
  }

  export interface InjectionDetailsWithId extends InjectionDetails {
    id: string | number;
  }

  export interface WellDetails {
    [wellPropertyNames.number]?: string;
    [wellPropertyNames.groundSurfaceElevation]?: string;
    [wellPropertyNames.wellDepth]?: string;
    [wellPropertyNames.wellCoordinates]?: FormSubmissionCoordinates;
    [wellPropertyNames.groundwaterDepth]?: string;
    [wellPropertyNames.perforatedCasingDepthStart]?: string;
    [wellPropertyNames.perforatedCasingTotalLength]?: string;
    [wellPropertyNames.openHoleStartDepth]?: string;
    [wellPropertyNames.openHoleTotalLength]?: string;
  }

  export interface WellDetailsWithId extends WellDetails {
    id: string | number;
  }

  export interface LocationDetails {
    [locationPropertyNames.facilityCoordinates]?: FormSubmissionCoordinates;
    [locationPropertyNames.quarterMileDrinkingWaterWell]?: FormSubmissionSelect;
    [locationPropertyNames.aboveBelowUicLine]?: FormSubmissionSelect;
    [locationPropertyNames.taxMapKeyNumber]?: string;
    [locationPropertyNames.tidallyInfluenced]?: FormSubmissionSelect;
    [locationPropertyNames.closestSurfaceWaterName]?: string;
    [locationPropertyNames.closestSurfaceWaterDistance]?: string;
    [locationPropertyNames.locatedSmaArea]?: FormSubmissionSelect;
  }

  export interface SpecialCommentsOrConsiderationsDetails {
    [specialCommentsOrConsiderationsPropertyNames.affectPublicTrustNativeHawaiianResource]?: FormSubmissionSelect;
    [specialCommentsOrConsiderationsPropertyNames.protectionFeasibleActions]?: string;
    [specialCommentsOrConsiderationsPropertyNames.additionalInformation]?: string;
  }

  export interface OperatorDetails {
    [operatorPropertyNames.operator]?: FormSubmissionContact;
  }

  export interface OwnerDetails {
    [ownerPropertyNames.owner]?: FormSubmissionContact;
  }

  export interface ConsultantDetails {
    [consultantPropertyNames.consultant]?: FormSubmissionContact;
  }

  export interface InjectionSystemDetails {
    [injectionSystemPropertyNames.injectionWellCount]?: number;
    [injectionSystemPropertyNames.injectedFluidSource]?: FormSubmissionSelect[]; //multi-select
    [injectionSystemPropertyNames.surfaceAreas]?: FormSubmissionSelect[]; //multi-select
    [injectionSystemPropertyNames.receivingWaterType]?: FormSubmissionSelect; //multi-select
  }

  //Section and Property Names
  export const sectionNames = {
    facilityDetails: "facilityDetails",
    injectionDetails: "injectionDetails",
    wellDetails: "wellDetails",
    locationDetails: "locationDetails",
    specialCommentsOrConsiderationsDetails: "specialCommentsOrConsiderationsDetails",
    operatorDetails: "operatorDetails",
    ownerDetails: "ownerDetails",
    consultantDetails: "consultantDetails",
    injectionSystemDetails: "injectionSystemDetails",
  } as const;

  type SectionNames =
    | typeof sectionNames.facilityDetails
    | typeof sectionNames.injectionDetails
    | typeof sectionNames.wellDetails
    | typeof sectionNames.locationDetails
    | typeof sectionNames.specialCommentsOrConsiderationsDetails
    | typeof sectionNames.operatorDetails
    | typeof sectionNames.ownerDetails
    | typeof sectionNames.consultantDetails
    | typeof sectionNames.injectionSystemDetails;

  export const facilityPropertyNames = {
    facilityName: "facilityName",
    permitNumber: "permitNumber",
    permitExpirationDate: "permitExpirationDate",
    uicPermitApplicability: "uicPermitApplicability",
    facilityInjectionWellSystemStatus: "facilityInjectionWellSystemStatus",
    epaIdentifier: "epaIdentifier",
  } as const;

  export const injectionPropertyNames = {
    wellNumber: "wellNumber",
    wellRate: "wellRate",
    injectionPressure: "injectionPressure",
    maxInjectionQuantity: "maxInjectionQuantity",
    maximumInjectionRate: "maximumInjectionRate",
    injectionManner: "injectionManner",
    waterTreatmentDescription: "waterTreatmentDescription",
  } as const;

  export const wellPropertyNames = {
    number: "number",
    groundSurfaceElevation: "groundSurfaceElevation",
    wellDepth: "wellDepth",
    wellCoordinates: "wellCoordinates",
    groundwaterDepth: "groundwaterDepth",
    perforatedCasingDepthStart: "perforatedCasingDepthStart",
    perforatedCasingTotalLength: "perforatedCasingTotalLength",
    openHoleStartDepth: "openHoleStartDepth",
    openHoleTotalLength: "openHoleTotalLength",
  } as const;

  export const locationPropertyNames = {
    facilityCoordinates: "facilityCoordinates",
    quarterMileDrinkingWaterWell: "quarterMileDrinkingWaterWell",
    aboveBelowUicLine: "aboveBelowUicLine",
    taxMapKeyNumber: "taxMapKeyNumber",
    tidallyInfluenced: "tidallyInfluenced",
    closestSurfaceWaterName: "closestSurfaceWaterName",
    closestSurfaceWaterDistance: "closestSurfaceWaterDistance",
    locatedSmaArea: "locatedSmaArea",
  } as const;

  export const specialCommentsOrConsiderationsPropertyNames = {
    affectPublicTrustNativeHawaiianResource: "affectPublicTrustNativeHawaiianResource",
    protectionFeasibleActions: "protectionFeasibleActions",
    additionalInformation: "additionalInformation",
  } as const;

  export const ownerPropertyNames = {
    owner: "owner",
  } as const;

  export const operatorPropertyNames = {
    operator: "operator",
  } as const;

  export const consultantPropertyNames = {
    consultant: "consultant",
  } as const;

  export const injectionSystemPropertyNames = {
    injectionWellCount: "injectionWellCount",
    injectedFluidSource: "injectedFluidSource",
    surfaceAreas: "surfaceAreas",
    receivingWaterType: "receivingWaterType",
  } as const;

  type PropertyNames =
    //Facility section
    | typeof facilityPropertyNames.facilityName
    | typeof facilityPropertyNames.permitNumber
    | typeof facilityPropertyNames.permitExpirationDate
    | typeof facilityPropertyNames.uicPermitApplicability
    | typeof facilityPropertyNames.facilityInjectionWellSystemStatus
    | typeof facilityPropertyNames.epaIdentifier

    //Injection for the Entire Drainage System Section
    | typeof injectionPropertyNames.wellNumber
    | typeof injectionPropertyNames.wellRate
    | typeof injectionPropertyNames.injectionPressure
    | typeof injectionPropertyNames.maxInjectionQuantity
    | typeof injectionPropertyNames.maximumInjectionRate
    | typeof injectionPropertyNames.injectionManner
    | typeof injectionPropertyNames.waterTreatmentDescription

    //Well Dimensions Section
    | typeof wellPropertyNames.number
    | typeof wellPropertyNames.groundSurfaceElevation
    | typeof wellPropertyNames.wellDepth
    | typeof wellPropertyNames.wellCoordinates
    | typeof wellPropertyNames.groundwaterDepth
    | typeof wellPropertyNames.perforatedCasingDepthStart
    | typeof wellPropertyNames.perforatedCasingTotalLength
    | typeof wellPropertyNames.openHoleStartDepth
    | typeof wellPropertyNames.openHoleTotalLength

    //LocationSection
    | typeof locationPropertyNames.facilityCoordinates
    | typeof locationPropertyNames.quarterMileDrinkingWaterWell
    | typeof locationPropertyNames.aboveBelowUicLine
    | typeof locationPropertyNames.taxMapKeyNumber
    | typeof locationPropertyNames.tidallyInfluenced
    | typeof locationPropertyNames.closestSurfaceWaterName
    | typeof locationPropertyNames.closestSurfaceWaterDistance
    | typeof locationPropertyNames.locatedSmaArea

    //SpecialCommentsOrConsiderationsSection
    | typeof specialCommentsOrConsiderationsPropertyNames.affectPublicTrustNativeHawaiianResource
    | typeof specialCommentsOrConsiderationsPropertyNames.protectionFeasibleActions
    | typeof specialCommentsOrConsiderationsPropertyNames.additionalInformation

    //OwnerSection
    | typeof ownerPropertyNames.owner

    //OperatorSection
    | typeof operatorPropertyNames.operator

    //ConsultantSection
    | typeof consultantPropertyNames.consultant

    //InjectionSystemSection
    | typeof injectionSystemPropertyNames.injectionWellCount
    | typeof injectionSystemPropertyNames.injectedFluidSource
    | typeof injectionSystemPropertyNames.surfaceAreas
    | typeof injectionSystemPropertyNames.receivingWaterType;

  //FormSectionMapping definition
  export interface FormSectionMapping {
    sections: SectionMapping[];
  }

  export interface SectionMapping {
    sectionPropertyName: SectionNames;
    sectionTag: string;
    sectionProperties: SectionPropertyMapping[];
    sectionIsList: boolean; //Flag to tell the parsing function not to store the values as a list. All the sections come back as lists, but sometimes we only have one item. It will grab the first item in the list.
  }

  export interface SectionPropertyMapping {
    propertyName: PropertyNames;
    propertyTag: string;
  }

  //Actual mapping for DrainageInjectionWellGeneral Application
  export const DrainageInjectionWellGeneralMapping: FormSectionMapping = {
    sections: [
      {
        sectionPropertyName: sectionNames.facilityDetails,
        sectionTag: TAGS.facilitySection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: facilityPropertyNames.facilityName,
            propertyTag: TAGS.facility_facilityName_Tag,
          },
          {
            propertyName: facilityPropertyNames.permitNumber,
            propertyTag: TAGS.facility_permitNumber_Tag,
          },
          {
            propertyName: facilityPropertyNames.permitExpirationDate,
            propertyTag: TAGS.facility_permitExpiration_Tag,
          },

          {
            propertyName: facilityPropertyNames.uicPermitApplicability,
            propertyTag: TAGS.facility_uicPermitApplicability_Tag,
          },
          {
            propertyName: facilityPropertyNames.facilityInjectionWellSystemStatus,
            propertyTag: TAGS.facility_facilityInjectionWellSystemStatus_Tag,
          },
          {
            propertyName: facilityPropertyNames.epaIdentifier,
            propertyTag: TAGS.facility_epaIdentifier_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.injectionDetails,
        sectionTag: TAGS.injectionSection_Tag,
        sectionIsList: true,
        sectionProperties: [
          {
            propertyName: injectionPropertyNames.wellNumber,
            propertyTag: TAGS.injection_wellNumber_Tag,
          },
          {
            propertyName: injectionPropertyNames.wellRate,
            propertyTag: TAGS.injection_wellRate_Tag,
          },
          {
            propertyName: injectionPropertyNames.injectionPressure,
            propertyTag: TAGS.injection_injectionPressure_Tag,
          },
          {
            propertyName: injectionPropertyNames.maxInjectionQuantity,
            propertyTag: TAGS.injection_maxInjectionQuantity_Tag,
          },
          {
            propertyName: injectionPropertyNames.maximumInjectionRate,
            propertyTag: TAGS.injection_maximumInjectionRate_Tag,
          },

          {
            propertyName: injectionPropertyNames.injectionManner,
            propertyTag: TAGS.injection_injectionManner_Tag,
          },
          {
            propertyName: injectionPropertyNames.waterTreatmentDescription,
            propertyTag: TAGS.injection_waterTreatmentDescription_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.wellDetails,
        sectionTag: TAGS.wellSection_Tag,
        sectionIsList: true,
        sectionProperties: [
          {
            propertyName: wellPropertyNames.number,
            propertyTag: TAGS.well_wellNumber_Tag,
          },
          {
            propertyName: wellPropertyNames.groundSurfaceElevation,
            propertyTag: TAGS.well_groundSurfaceElevation_Tag,
          },
          {
            propertyName: wellPropertyNames.wellDepth,
            propertyTag: TAGS.well_wellDepth_Tag,
          },
          {
            propertyName: wellPropertyNames.wellCoordinates,
            propertyTag: TAGS.well_wellCoordinates_Tag,
          },
          {
            propertyName: wellPropertyNames.groundwaterDepth,
            propertyTag: TAGS.well_groundwaterDepth_Tag,
          },

          {
            propertyName: wellPropertyNames.perforatedCasingDepthStart,
            propertyTag: TAGS.well_perforatedCasingDepthStart_Tag,
          },
          {
            propertyName: wellPropertyNames.perforatedCasingTotalLength,
            propertyTag: TAGS.well_perforatedCasingTotalLength_Tag,
          },
          {
            propertyName: wellPropertyNames.openHoleStartDepth,
            propertyTag: TAGS.well_openHoleStartDepth_Tag,
          },
          {
            propertyName: wellPropertyNames.openHoleTotalLength,
            propertyTag: TAGS.well_openHoleTotalLength_Tag,
          },
        ],
      },

      {
        sectionPropertyName: sectionNames.locationDetails,
        sectionTag: TAGS.locationSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: locationPropertyNames.facilityCoordinates,
            propertyTag: TAGS.location_facilityCoordinates_Tag,
          },
          {
            propertyName: locationPropertyNames.quarterMileDrinkingWaterWell,
            propertyTag: TAGS.location_quarterMileDrinkingWaterWell_Tag,
          },
          {
            propertyName: locationPropertyNames.aboveBelowUicLine,
            propertyTag: TAGS.location_aboveBelowUicLine_Tag,
          },

          {
            propertyName: locationPropertyNames.taxMapKeyNumber,
            propertyTag: TAGS.location_taxMapKeyNumber_Tag,
          },
          {
            propertyName: locationPropertyNames.tidallyInfluenced,
            propertyTag: TAGS.location_tidallyInfluenced_Tag,
          },
          {
            propertyName: locationPropertyNames.closestSurfaceWaterName,
            propertyTag: TAGS.location_closestSurfaceWaterName_Tag,
          },
          {
            propertyName: locationPropertyNames.closestSurfaceWaterDistance,
            propertyTag: TAGS.location_closestSurfaceWaterDistance_Tag,
          },
          {
            propertyName: locationPropertyNames.locatedSmaArea,
            propertyTag: TAGS.location_locatedSmaArea_Tag,
          },
        ],
      },

      {
        sectionPropertyName: sectionNames.specialCommentsOrConsiderationsDetails,
        sectionTag: TAGS.specialCommentsOrConsiderationsSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: specialCommentsOrConsiderationsPropertyNames.affectPublicTrustNativeHawaiianResource,
            propertyTag: TAGS.specialCommentsOrConsiderations_affectPublicTrustNativeHawaiianResource_Tag,
          },
          {
            propertyName: specialCommentsOrConsiderationsPropertyNames.protectionFeasibleActions,
            propertyTag: TAGS.specialCommentsOrConsiderations_protectionFeasibleActions_Tag,
          },
          {
            propertyName: specialCommentsOrConsiderationsPropertyNames.additionalInformation,
            propertyTag: TAGS.specialCommentsOrConsiderations_additionalInformation_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.ownerDetails,
        sectionTag: TAGS.ownerSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: ownerPropertyNames.owner,
            propertyTag: TAGS.owner_owner_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.operatorDetails,
        sectionTag: TAGS.operatorSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: operatorPropertyNames.operator,
            propertyTag: TAGS.oeprator_operator_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.consultantDetails,
        sectionTag: TAGS.consultantSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: consultantPropertyNames.consultant,
            propertyTag: TAGS.consultant_consultant_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.injectionSystemDetails,
        sectionTag: TAGS.injectionSystemSectionSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: injectionSystemPropertyNames.injectionWellCount,
            propertyTag: TAGS.injectionSystemSection_injectionWellCount_Tag,
          },
          {
            propertyName: injectionSystemPropertyNames.injectedFluidSource,
            propertyTag: TAGS.injectionSystemSection_injectedFluidSource_Tag,
          },
          {
            propertyName: injectionSystemPropertyNames.surfaceAreas,
            propertyTag: TAGS.injectionSystemSection_surfaceAreas_Tag,
          },
          {
            propertyName: injectionSystemPropertyNames.receivingWaterType,
            propertyTag: TAGS.injectionSystemSection_receivingWaterType_Tag,
          },
        ],
      },
    ],
  };
}
